import { morph, MORPH_OPTIONS } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Fetches a product page and parses it into a Document. Shared by
 * `QuickAddComponent` (opening the modal for the card's own product) and
 * `QuickAddDialog` (swapping an already-open modal to a colorway sibling).
 * @param {string} url
 * @param {AbortSignal} signal
 * @returns {Promise<Document | null>}
 */
async function fetchProductDocument(url, signal) {
  if (!url) return null;

  try {
    const response = await fetch(url, { signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  } catch (error) {
    if (error.name === 'AbortError') {
      return null;
    } else {
      throw error;
    }
  }
}

/**
 * Restructures fetched product grid markup for the mobile quick-add layout:
 * pulls the title/price/variant-picker/buy-buttons out of `.product-details`
 * into a flat `.product-header`, since the mobile modal doesn't use the
 * two-column desktop layout `.product-details` assumes.
 * @param {Element} productGrid
 * @param {{ title: string, url: string }} product
 */
function restructureForMobileQuickAdd(productGrid, product) {
  const productDetails = productGrid.querySelector('.product-details');
  const productFormComponent = productGrid.querySelector('product-form-component');
  const variantPicker = productGrid.querySelector('variant-picker');
  const productPrice = productGrid.querySelector('product-price');
  const productTitle = document.createElement('a');
  productTitle.textContent = product.title;
  productTitle.href = product.url;

  const productHeader = document.createElement('div');
  productHeader.classList.add('product-header');

  productHeader.appendChild(productTitle);
  if (productPrice) {
    productHeader.appendChild(productPrice);
  }
  productGrid.appendChild(productHeader);

  if (variantPicker) {
    productGrid.appendChild(variantPicker);
  }
  if (productFormComponent) {
    productGrid.appendChild(productFormComponent);
  }

  productDetails?.remove();
}

/**
 * Forces every variant radio's checked state (and the pill-animation flags
 * `data-current-checked`/`data-previous-checked`) in `modalContent` to match
 * the freshly-fetched, never-mutated `sourceGrid` - keyed by
 * `data-option-value-id`, which is stable and unique per option value,
 * instead of trusting `morph()`'s own node matching.
 *
 * `morph()` has no `id`-based key for these radios (Liquid doesn't render
 * one), so it falls back to matching same-tag nodes by position. Across two
 * different products that's unreliable enough that a swap could leave a
 * radio's `checked`/`data-current-checked` mismatched with its own value -
 * seen in practice as the "selected" pill landing on the wrong button (or
 * several) after a colorway swap. Re-deriving state from `sourceGrid` by key
 * sidesteps that positional guessing entirely.
 * @param {Element} modalContent
 * @param {Element} sourceGrid
 */
function resyncVariantCheckedState(modalContent, sourceGrid) {
  const freshInputs = sourceGrid.querySelectorAll('input[type="radio"][data-option-value-id]');

  for (const freshInput of freshInputs) {
    if (!(freshInput instanceof HTMLInputElement)) continue;

    const key = freshInput.dataset.optionValueId;
    if (!key) continue;

    const liveInput = modalContent.querySelector(`input[type="radio"][data-option-value-id="${CSS.escape(key)}"]`);
    if (!(liveInput instanceof HTMLInputElement)) continue;

    if (liveInput.checked !== freshInput.checked) {
      liveInput.checked = freshInput.checked;
    }

    liveInput.dataset.currentChecked = freshInput.dataset.currentChecked ?? 'false';
    liveInput.dataset.previousChecked = freshInput.dataset.previousChecked ?? 'false';
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      return await fetchProductDocument(productPageUrl, this.#abortController.signal);
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      restructureForMobileQuickAdd(productGrid, {
        title: this.dataset.productTitle || '',
        url: this.productPageUrl,
      });
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #colorwayAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#colorwayAbortController?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Swaps the quick-add modal to a sibling "colorway" product (see
   * `blocks/colorway-picker.liquid`) in place, without closing the dialog or
   * navigating away. Reached via that block's `on:click="/handleColorwaySelect"`:
   * once its markup is fetched into `#quick-add-modal-content`, the closest
   * custom-element ancestor of the swatch is this `<quick-add-dialog>`, so
   * clicks route here instead of following the link.
   *
   * Mirrors `QuickAddComponent.handleClick`'s fetch + morph pattern, just
   * targeting the sibling product's URL instead of the card's own product.
   * @param {MouseEvent} event
   */
  handleColorwaySelect = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('a.colorway-picker__swatch');

    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    this.#colorwayAbortController?.abort();
    const controller = new AbortController();
    this.#colorwayAbortController = controller;

    const html = await fetchProductDocument(link.href, controller.signal);

    if (!html || controller.signal.aborted) return;

    const gridElement = html.querySelector('[data-product-grid-content]');
    const modalContent = this.querySelector('#quick-add-modal-content');

    if (!gridElement || !modalContent) return;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    const titleAnchor = /** @type {HTMLAnchorElement | null} */ (html.querySelector('.view-product-title a'));

    if (isMobileBreakpoint()) {
      restructureForMobileQuickAdd(productGrid, {
        title: titleAnchor?.textContent?.trim() || '',
        url: titleAnchor?.href || link.href,
      });
    }

    // Plain MORPH_OPTIONS carries a few attributes (data-current-checked,
    // data-previous-checked, ...) forward from the old node onto the new one
    // when they differ, so the variant-picker's "selected pill" can animate
    // smoothly between two states of the SAME product's options. Here the old
    // and new grids belong to two completely different products with
    // unrelated option trees, so that carry-over just corrupts the new
    // product's checked/pill state (e.g. an old size's data-current-checked
    // sticking on a same-position button of the new size list). Keep the node
    // rejection rules and the post-morph Component refresh, drop the
    // attribute/style carry-over.
    morph(modalContent, productGrid, {
      childrenOnly: true,
      reject: MORPH_OPTIONS.reject,
      onAfterUpdate: MORPH_OPTIONS.onAfterUpdate,
    });

    // Belt-and-braces: morph() has no stable key for these radios (Liquid
    // doesn't render an `id`), so across two different products its
    // same-tag/positional matching can leave a radio's checked state or pill
    // flags out of sync with its own value. Re-derive them directly from
    // `gridElement`, the untouched fetch result, keyed by the option value's
    // own id instead of DOM position.
    resyncVariantCheckedState(modalContent, gridElement);

    this.refs.dialog?.querySelector('.product-details')?.scrollTo({ top: 0, behavior: 'instant' });
    this.refs.dialog?.querySelector('.product-information__media')?.scrollTo({ top: 0, behavior: 'instant' });
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
